import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';

import type { AbstractError } from '@/domain/errors/abstract-error.js';
import type { JiraEpicOption } from '@/domain/models/development.js';
import { readOccupiedEpicOwner } from '@/presentation/pages/developments/development-detail/utils/read-occupied-epic-owner.js';

interface Labels {
    title: string;
    suggestionLabel: string;
    listLabel: string;
    confirmLabel: string;
    noPairLabel: string;
    cancelLabel: string;
    emptyLabel: string;
    loadingLabel: string;
    searchLabel: string;
    occupiedWarning: (displayId: string) => string;
}

interface Props {
    open: boolean;
    suggestion: JiraEpicOption | null;
    epics: JiraEpicOption[];
    occupiedError: AbstractError | null;
    failureMessage: string | null;
    loading: boolean;
    submitting: boolean;
    labels: Labels;
    onConfirm: (epicKey: string) => void;
    onNoPair: () => void;
    onClose: () => void;
}

export function AssistedJiraLinkDialog({ open, suggestion, epics, occupiedError, failureMessage, loading, submitting, labels, onConfirm, onNoPair, onClose }: Props) {
    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const [query, setQuery] = useState('');
    const confirmKey = selectedKey ?? suggestion?.key ?? null;
    const occupied = occupiedError ? readOccupiedEpicOwner(occupiedError) : null;
    const busy = loading || submitting;
    const needle = query.trim().toLowerCase();
    const visibleEpics = needle === '' ? epics : epics.filter((epic) => epic.key.toLowerCase().includes(needle) || epic.summary.toLowerCase().includes(needle));

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{labels.title}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {loading && <Typography data-testid="assisted-jira-link-dialog-loading">{labels.loadingLabel}</Typography>}
                    {occupied && (
                        <Alert severity="warning" data-testid="assisted-jira-link-dialog-occupied">
                            {labels.occupiedWarning(occupied.displayId)}
                        </Alert>
                    )}
                    {!occupied && (failureMessage || occupiedError) && (
                        <Alert severity="error" data-testid="assisted-jira-link-dialog-failure">
                            {failureMessage ?? occupiedError?.message}
                        </Alert>
                    )}
                    {suggestion && (
                        <Stack spacing={0.5} data-testid="assisted-jira-link-dialog-suggestion">
                            <Typography variant="subtitle2">{labels.suggestionLabel}</Typography>
                            <Typography data-testid="assisted-jira-link-dialog-suggestion-key">{suggestion.key}</Typography>
                            <Typography data-testid="assisted-jira-link-dialog-suggestion-summary">{suggestion.summary}</Typography>
                        </Stack>
                    )}
                    <TextField
                        data-testid="assisted-jira-link-dialog-search"
                        label={labels.searchLabel}
                        value={query}
                        size="small"
                        onChange={(event) => setQuery(event.target.value)}
                    />
                    <Stack spacing={1}>
                        <Typography variant="subtitle2">{labels.listLabel}</Typography>
                        {visibleEpics.length === 0 && !loading && <Typography data-testid="assisted-jira-link-dialog-empty">{labels.emptyLabel}</Typography>}
                        {visibleEpics.map((epic) => (
                            <Button
                                key={epic.key}
                                data-testid={`assisted-jira-link-dialog-epic-${epic.key}`}
                                variant={selectedKey === epic.key ? 'contained' : 'outlined'}
                                disabled={busy}
                                onClick={() => setSelectedKey(epic.key)}
                            >
                                {`${epic.key} ${epic.summary}`}
                            </Button>
                        ))}
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button data-testid="assisted-jira-link-dialog-cancel" onClick={onClose} disabled={submitting}>
                    {labels.cancelLabel}
                </Button>
                <Button data-testid="assisted-jira-link-dialog-no-pair" onClick={onNoPair} disabled={busy}>
                    {labels.noPairLabel}
                </Button>
                <Button
                    data-testid="assisted-jira-link-dialog-confirm"
                    variant="contained"
                    disabled={busy || !confirmKey}
                    onClick={() => {
                        if (confirmKey) {
                            onConfirm(confirmKey);
                        }
                    }}
                >
                    {labels.confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
